import { useCallback, useRef } from "react";

interface QueueItem {
  id: string;
  callback: () => Promise<void>;
}

/**
 * Processes a queue of callbacks in order and sequentially without parallel execution.
 * Deduplication of queued callbacks can be configured.
 */
export default function useCallbackQueue({
  onQueuedFinished,
  onCallbackFinished,
  deduplicationStrategy,
}: {
  onQueuedFinished?: () => void;
  onCallbackFinished?: (id: string, queue: QueueItem[]) => void;
  deduplicationStrategy?: "keep-last";
} = {}) {
  const queue = useRef<QueueItem[]>([]);
  const curentlyProcessingId = useRef<string>(undefined);

  const processQueue = useCallback(async () => {
    if (curentlyProcessingId.current || queue.current.length === 0) return;

    while (queue.current.length > 0) {
      const { id, callback } = queue.current.shift();

      curentlyProcessingId.current = id;
      if (callback) {
        try {
          await callback();
        } catch (err) {
          console.error(`Error processing callback for id ${id}:`, err);
        }
      }

      // Example: A, B, C, C, C, B, A.
      // Result: C, B, A.
      if (deduplicationStrategy === "keep-last") {
        const seen = new Set<string>();
        queue.current = queue.current
          .reverse()
          .filter((item) => {
            if (seen.has(item.id)) {
              return false;
            }
            seen.add(item.id);
            return true;
          })
          .reverse();
      }

      onCallbackFinished?.(id, queue.current);
    }

    if (onQueuedFinished) {
      onQueuedFinished();
    }
    curentlyProcessingId.current = undefined;
  }, [onQueuedFinished]);

  const addToQueue = useCallback(
    (id: string, callback: () => Promise<void>) => {
      queue.current.push({ id, callback });
      void processQueue();
    },
    [processQueue]
  );

  return {
    addToQueue,
  };
}
